import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Plus, UserMinus, UserSearch } from "lucide-mui";
import type { ReactNode } from "react";
import type {
  ContactPurchase,
  ProspectListContactEntry,
} from "../../../api/client.ts";
import ReviewToggle from "../../../components/ReviewToggle.tsx";
import RowActions, { type RowAction } from "../../../components/RowActions.tsx";
import SectionHeading from "../../../components/SectionHeading.tsx";
import StatusChip from "../../../components/StatusChip.tsx";
import { formatMoney } from "../../../utils/format.ts";
import ContactCard from "../../contacts/ContactCard.tsx";
import type { ContactEntryMutations } from "./useContactEntryMutations.ts";
import type { CrmStatusResult } from "./useCrmStatus.ts";
import { type CollectionCompany, getProspectStatus } from "./entryStatus.ts";
import ContactNoteCell from "./ContactNoteCell.tsx";
import CrmStatusCell from "./CrmStatusCell.tsx";

interface CompanyContactsPanelProps<T extends CollectionCompany> {
  /** The heading over the cards ("Contacts in <collection>"). */
  title: ReactNode;
  prospect: T;
  contacts: ProspectListContactEntry[];
  loading: boolean;
  mutations: ContactEntryMutations;
  crm: CrmStatusResult;
  onAddContact: (prospect: T) => void;
  /** Set only when the user may buy contact data for the selected organization. */
  onFindContacts?: (prospect: T) => void;
  /** The latest contact purchase for this company, if any. */
  purchase?: ContactPurchase;
  /** The entry whose person is open beside the drawer. */
  openEntryId?: string | null;
  /** Opens the person; `el` is their card. */
  onOpenContact: (contact: ProspectListContactEntry, el: HTMLElement) => void;
}

function PurchaseChip({ purchase }: { purchase: ContactPurchase }) {
  // A free provider's purchase (GetLeads) charges nothing: no "$0.00".
  const cost = purchase.charged_amount > 0 ? ` · ${formatMoney(purchase.charged_amount, purchase.currency)}` : "";
  switch (purchase.status) {
    case "pending":
    case "running":
      return <StatusChip kind="job" value={purchase.status} label="Finding contacts…" variant="outlined" />;
    case "completed":
      return (
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label={`Bought ${purchase.found_count}${cost}`}
        />
      );
    case "partial":
      return (
        <Tooltip title={purchase.error ?? ""}>
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`Partly done: ${purchase.found_count}${cost}`}
          />
        </Tooltip>
      );
    case "failed":
      return (
        <Tooltip title={purchase.error ?? ""}>
          <Chip size="small" color="error" variant="outlined" label="Purchase failed" />
        </Tooltip>
      );
  }
}

/**
 * The people at one company on a collection, as contact cards with the
 * collection's controls on each (decision, remove, CRM state, note) and the
 * collection's contact actions over them (add one, find some).
 */
export default function CompanyContactsPanel<T extends CollectionCompany>({
  title,
  prospect,
  contacts,
  loading,
  mutations,
  crm,
  onAddContact,
  onFindContacts,
  purchase,
  openEntryId,
  onOpenContact,
}: CompanyContactsPanelProps<T>) {
  const removeAction = (c: ProspectListContactEntry): RowAction => ({
    label: "Remove from collection",
    icon: <UserMinus fontSize="small" />,
    destructive: true,
    onClick: () => mutations.remove.mutateAsync(c.id),
    confirm: {
      title: `Remove ${c.contact_name ?? "this contact"} from the collection?`,
      body: "The person stays in the database; only their entry in this collection goes.",
      confirmLabel: "Remove",
    },
  });
  const purchaseOpen = purchase?.status === "pending" || purchase?.status === "running";

  return (
    <>
      <SectionHeading
        title={title}
        count={contacts.length}
        action={
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            {purchase && <PurchaseChip purchase={purchase} />}
            {onFindContacts && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<UserSearch fontSize="small" />}
                onClick={() => onFindContacts(prospect)}
                disabled={purchaseOpen}
              >
                Find contacts
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<Plus fontSize="small" />}
              onClick={() => onAddContact(prospect)}
            >
              Add contact
            </Button>
          </Stack>
        }
      />

      {loading && contacts.length === 0 ? (
        <Skeleton variant="rounded" height={72} />
      ) : contacts.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {onFindContacts
            ? "No contacts yet — find some with a data provider, or add one by hand."
            : "No contacts yet — add one to start working this account."}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {contacts.map((c) => {
            const status = getProspectStatus(c);
            return (
              <ContactCard
                key={c.id}
                contact={c}
                selected={c.id === openEntryId}
                onOpen={(el) => onOpenContact(c, el)}
                controls={
                  <>
                    <ReviewToggle value={status} onChange={(next) => mutations.setStatus(c.id, status, next)} />
                    <RowActions label={c.contact_name ?? "contact"} actions={[removeAction(c)]} />
                  </>
                }
                footer={
                  <Stack spacing={0.75}>
                    {c.inclusion_reason && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {c.inclusion_reason}
                      </Typography>
                    )}
                    {crm.available && <CrmStatusCell status={crm.byEntry.get(c.id)} />}
                    <ContactNoteCell
                      entry={c}
                      onSave={(note) => mutations.updateNote.mutate({ entryId: c.id, note })}
                    />
                  </Stack>
                }
              />
            );
          })}
        </Stack>
      )}
    </>
  );
}
